import { Autocomplete, Chip, TextField, Tooltip } from "@mui/material";

import { useField } from "formik";
import { useRef, useState, useEffect } from "react";

export const MultiSelectFormControl = ({
  id,
  label,
  options,
  value,
  handleSubmit,
  getOptionLabel,
  getOptionValue,
  displayField = "displayName", // Control which field to display: "value" or "displayName"
}: any) => {
  const [, , helpers] = useField(id);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [limitTags, setLimitTags] = useState(1);

  // Handle both string arrays and object arrays
  const isObjectOptions = options.length > 0 && typeof options[0] === "object";

  const defaultGetOptionLabel = (option: any) => {
    // Use displayField prop to control which field to show
    if (getOptionLabel) return getOptionLabel(option);
    if (typeof option === "string") return option;
    return option[displayField] || option.displayName || option;
  };

  const defaultGetOptionValue = (option: any) => {
    if (typeof option === "string") return option;
    return getOptionValue ? getOptionValue(option) : option.value || option;
  };

  // For object options, need to match values to options
  const selectedOptions = isObjectOptions
    ? options.filter((opt: any) => value.includes(defaultGetOptionValue(opt)))
    : value;

  // Calculate how many tags can fit based on container width
  useEffect(() => {
    const calculateLimitTags = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      // Account for padding, icons (dropdown + clear), and input field minimum width
      const availableWidth = containerWidth - 100; // Reserve space for controls
      const avgTagWidth = 80; // Average tag width including margin
      const calculatedLimit = Math.max(
        1,
        Math.floor(availableWidth / avgTagWidth),
      );

      setLimitTags(Math.min(calculatedLimit, selectedOptions.length));
    };

    calculateLimitTags();
    window.addEventListener("resize", calculateLimitTags);

    return () => window.removeEventListener("resize", calculateLimitTags);
  }, [selectedOptions.length]);

  // Recalculate when selected options change
  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const availableWidth = containerWidth - 100;
      const avgTagWidth = 80;
      const calculatedLimit = Math.max(
        1,
        Math.floor(availableWidth / avgTagWidth),
      );
      setLimitTags(Math.min(calculatedLimit, selectedOptions.length));
    }
  }, [selectedOptions]);

  return (
    <Autocomplete
      ref={containerRef}
      id={id}
      multiple
      open={open}
      limitTags={limitTags}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      size="small"
      sx={{
        width: "100%",
        "&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root":
          {
            paddingRight: 0,
          },
        "& .MuiOutlinedInput-root": {
          flexWrap: "nowrap",
          overflow: "hidden",
        },
        "& .MuiAutocomplete-tag": {
          maxWidth: "120px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          height: "25px",
          fontSize: "0.7rem",
          "& .MuiChip-label": { padding: "0 6px" },
          "& .MuiChip-deleteIcon": { fontSize: "0.85rem" },
        },
        "& .MuiAutocomplete-popupIndicator": {
          padding: "2px",
          "& svg": { fontSize: "1rem" },
        },
        "& .MuiAutocomplete-clearIndicator": {
          padding: "2px",
          "& svg": { fontSize: "1rem" },
        },
      }}
      getOptionLabel={defaultGetOptionLabel}
      isOptionEqualToValue={(option, value) => {
        return defaultGetOptionValue(option) === defaultGetOptionValue(value);
      }}
      onChange={(_, newValue) => {
        const valuesToStore = isObjectOptions
          ? newValue.map((item: any) => defaultGetOptionValue(item))
          : newValue;
        helpers.setValue(valuesToStore || []);
        handleSubmit();
      }}
      options={options}
      value={selectedOptions}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Tooltip key={key} title={defaultGetOptionLabel(option)} arrow>
              <Chip {...tagProps} label={defaultGetOptionLabel(option)} />
            </Tooltip>
          );
        })
      }
      renderInput={(params) => (
        <TextField {...params} inputRef={inputRef} label={label} />
      )}
    />
  );
};
